import CachingServiceLocalStorageEntity from "./entity/local-storage";
import {
    CachingServiceCookiesKeys,
    CachingServiceCookiesKeysType,
    CachingServiceEntities,
    CachingServiceEntitiesType,
    CachingServiceLocalStorageKeys,
    CachingServiceLocalStorageKeysType
} from "./type-declarations";
import CachingServiceCookiesEntity from "./entity/cookies";
import {ThemeColorSchemes} from "../../../ui";


/**
 * Houses the logic for caching information in the user browser.
 * This wrapper provides the requested storage entity through its .of() method.
 */
class CachingService {

    /**
     * The local-storage entity of this Caching service.
     * @private
     */
    protected static LocalStorageEntity = new CachingServiceLocalStorageEntity<CachingServiceLocalStorageKeysType>({
        [CachingServiceLocalStorageKeys.loggedInUser]: 'logged-in-user',
        [CachingServiceLocalStorageKeys.partnerColorScheme]: 'partner-color-scheme',
    });

    /**
     * The cookies entity of this Caching service.
     * @private
     */
    protected static CookiesEntity = new CachingServiceCookiesEntity<CachingServiceCookiesKeysType>({
        [CachingServiceCookiesKeys.loggedInUser]: 'logged-in-user',
        [CachingServiceCookiesKeys.partnerColorScheme]: 'partner-color-scheme',
    });


    /**
     * Fetches the cookies entity of this Caching service.
     * @param entityType
     */
    public static of(entityType: CachingServiceEntities.cookies): CachingServiceCookiesEntity<CachingServiceCookiesKeys>;
    /**
     * Fetches the local-storage entity of this Caching service.
     * @param entityType
     */
    public static of(entityType: CachingServiceEntities.localStorage): CachingServiceLocalStorageEntity<CachingServiceLocalStorageKeys>;
    /**
     * Fetches the requested entity of this Caching service based on the provided entityType.
     * @param entityType
     */
    public static of(entityType: CachingServiceEntities) {
        switch (entityType) {
            case CachingServiceEntities.cookies:
                return this.CookiesEntity;
            case CachingServiceEntities.localStorage:
                return this.LocalStorageEntity;
            default:
                throw new Error("Wrong Caching Service Entity is requested.");
        }
    }

    /**
     * Fetches the value of the partner's color-scheme key.
     * @param instance  The instance to get the partner's color-scheme from.
     */
    public static getPartnerColorScheme(instance: CachingServiceEntitiesType = CachingServiceEntities.localStorage): ThemeColorSchemes | undefined {
        let result: string | undefined;
        switch (instance) {
            case 'localStorage':
                result = this.of(CachingServiceEntities.localStorage).get(CachingServiceLocalStorageKeys.partnerColorScheme) ?? undefined;
                break;
            case 'cookies':
                result = this.of(CachingServiceEntities.cookies).get(CachingServiceCookiesKeys.partnerColorScheme) ?? undefined;
                break;
        }
        if (result === undefined)
            return undefined;
        return JSON.parse(result) as ThemeColorSchemes;
    }

    /**
     * Sets the value of the partner's color-scheme key.
     * @param instance  The instance to set the partner's color-scheme in.
     * @param value     The value to set.
     */
    public static setPartnerColorScheme(value: ThemeColorSchemes | undefined, instance: CachingServiceEntitiesType = CachingServiceEntities.localStorage) {
        if (value === undefined) {
            switch (instance) {
                case 'localStorage':
                    return this.of(CachingServiceEntities.localStorage).remove(CachingServiceLocalStorageKeys.partnerColorScheme);
                case 'cookies':
                    return this.of(CachingServiceEntities.cookies).remove(CachingServiceLocalStorageKeys.partnerColorScheme);
            }
            return;
        }
        switch (instance) {
            case 'localStorage':
                return this.of(CachingServiceEntities.localStorage).set(CachingServiceLocalStorageKeys.partnerColorScheme, JSON.stringify(value));
            case 'cookies':
                return this.of(CachingServiceEntities.cookies).set(CachingServiceCookiesKeys.partnerColorScheme, JSON.stringify(value));
        }
    }
}

export * from "./entity/local-storage";
export * from "./entity/cookies";
export * from "./type-declarations";
export default CachingService;
